import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    Table,
    TableBody,
    TableContainer,
    Typography,
    TextField,
    InputAdornment,
    CircularProgress,
    Box,
    Stack,
} from '@mui/material';
import {
    TableNoData,
    TableHeadCustom,
    TablePaginationCustom,
} from 'src/components/table';
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import DynamicTableRow from 'src/sections/@dashboard/DynamicTableRow';
import Label from 'src/components/label';
import { fCurrencyIndo } from 'src/utils/formatNumber';
import { PATH_MASTERDATA } from 'src/routes/paths';
import userAPI from 'src/api/userAPI';
import usePaginatedTable from 'src/hooks/usePaginatedTable';

// ----------------------------------------------------------------------

const TABLE_HEAD = [
    { id: 'name', label: 'Name', align: 'center' },
    { id: 'nik', label: 'NIK', align: 'left' },
    { id: 'phone', label: 'Phone', align: 'center' },
    { id: 'total_deposit', label: 'Deposit', align: 'center' },
    { id: 'status', label: 'Status', align: 'center' },
];

// ----------------------------------------------------------------------

SiteCourierList.propTypes = {
    siteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default function SiteCourierList({ siteId }) {
    const navigate = useNavigate();

    // ✅ Gunakan hook universal untuk pagination, sorting, filter
    const {
        data: tableData,
        loading,
        pagination,
        setPage,
        setPerPage,
        filters,
        setFilterValue,
        sort,
        handleSort,
    } = usePaginatedTable({
        apiFn: userAPI.getCourier,
        params: { site_id: siteId, role_id: 3, sort_by: 'total_deposit', sort: 'desc' },
        initialFilter: { search: '' },
    });

    // 🔍 Search handler
    const handleSearchChange = (e) => {
        setPage(1);
        setFilterValue('search', e.target.value);
    };

    const isNotFound = !loading && tableData.length === 0;

    // ----------------------------------------------------------------------

    return (
        <>
            {/* 🔹 Header */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Typography variant="h5">Courier List</Typography>
            </Stack>

            {/* 🔍 Search */}
            <TextField
                fullWidth
                placeholder="Search by name, NIK, or phone..."
                value={filters.search || ''}
                onChange={handleSearchChange}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
                        </InputAdornment>
                    ),
                }}
            />

            {/* 🔹 Table */}
            <Card sx={{ mt: 2 }}>
                <TableContainer sx={{ position: 'relative', overflow: 'unset' }}>
                    {loading ? (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: 300,
                                flexDirection: 'column',
                            }}
                        >
                            <CircularProgress color="primary" />
                            <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                                Loading courier data...
                            </Typography>
                        </Box>
                    ) : (
                        <Scrollbar>
                            <Table size="small" sx={{ minWidth: 600 }}>
                                <TableHeadCustom
                                    order={sort.orderDirection}
                                    orderBy={sort.orderBy}
                                    headLabel={TABLE_HEAD}
                                    onSort={handleSort}
                                />
                                <TableBody>
                                    {tableData.map((row) => (
                                        <DynamicTableRow
                                            key={row.id}
                                            row={{
                                                id: row.id,
                                                name: row.name,
                                                nik: row.nik,
                                                phone: row.phone,
                                                total_deposit: fCurrencyIndo(row.total_deposit),
                                                status: (
                                                    <Label color={row.is_active ? 'success' : 'error'}>
                                                        {row.is_active ? 'Active' : 'Inactive'}
                                                    </Label>
                                                ),
                                            }}
                                            columns={TABLE_HEAD}
                                            enableAvatar
                                            controlEditDelete={false}
                                            onRowClick={() => navigate(PATH_MASTERDATA.userDetail(row.id))}
                                        />
                                    ))}
                                    <TableNoData isNotFound={isNotFound} />
                                </TableBody>
                            </Table>
                        </Scrollbar>
                    )}
                </TableContainer>

                {/* 🔹 Pagination */}
                <TablePaginationCustom
                    count={pagination.total}
                    page={pagination.page - 1}
                    rowsPerPage={pagination.perPage}
                    onPageChange={(_, newPage) => setPage(newPage + 1)}
                    onRowsPerPageChange={(e) => setPerPage(parseInt(e.target.value, 10))}
                />
            </Card>
        </>
    );
}
